import React, { useState, useEffect, useCallback } from 'react';
import { 
  getJiraProjects, 
  getJiraPriorities, 
  createJiraTicket,
  createBulkJiraTickets,
  checkJiraStatus,
  checkPriorityAvailability
} from '../../../api/integrations';

const JiraTicketModal = ({ 
  isOpen, 
  onClose, 
  selectedTestCases = [], 
  singleTestCaseId = null,
  testCaseDetails = null,
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [jiraConnected, setJiraConnected] = useState(true);
  const [jiraProjects, setJiraProjects] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [createdTickets, setCreatedTickets] = useState([]);
  const [useSameSettings, setUseSameSettings] = useState(true);
  const [progress, setProgress] = useState({ current: 0, total: 0, inProgress: false });

  // Form state
  const [selectedProject, setSelectedProject] = useState("");
  const [commonSettings, setCommonSettings] = useState({
    title: "",
    priority: "",
    additionalDetails: ""
  });
  
  // State for individual test case settings
  const [individualSettings, setIndividualSettings] = useState({});

  // Check if we're in single test case mode
  const isSingleMode = !!singleTestCaseId;
  
  // Count of test cases being processed
  const testCaseCount = isSingleMode ? 1 : selectedTestCases.length;

  // Add a flag to track if an API request is in progress
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Add loading states for different data types
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [prioritiesLoading, setPrioritiesLoading] = useState(false);

  // Initialize with priority enabled
  const [isPriorityAvailable, setIsPriorityAvailable] = useState(true);
  const [priorityChecked, setPriorityChecked] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    if (isOpen && !isLoadingData) {
      setIsLoadingData(true);
      
      // First check connection
      checkJiraConnection()
        .then(() => {
          // Only fetch data if component is still mounted and we're connected
          if (isMounted && jiraConnected) {
            return fetchJiraData();
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoadingData(false);
          }
        });
      
      // Initialize individual settings for each test case
      if (!isSingleMode) {
        const settings = {};
        selectedTestCases.forEach(tc => {
          const defaultTitle = tc.testname ? 
            `Issue with test: ${tc.testname}` : 
            `Test case ${tc.id} issue`;
            
          const defaultDetails = tc.message ? 
            `test case with the message: ${tc.message}` : 
            "No error details available for this test case";
            
          settings[tc.id] = {
            title: defaultTitle,
            priority: "",
            additionalDetails: defaultDetails
          };
        });
        setIndividualSettings(settings);
      }

      // If in single mode, pre-fill with test case details
      if (isSingleMode && testCaseDetails) {
        const defaultTitle = testCaseDetails.testname ? 
          `Issue with test: ${testCaseDetails.testname}` : 
          "Test case issue";
        
        setCommonSettings({
          title: defaultTitle,
          priority: "",
          additionalDetails: `Test case '${testCaseDetails.testname}' failed with the message: ${testCaseDetails.message || 'No error message'}`
        });
      }
    }
    
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const checkJiraConnection = async () => {
    try {
      const status = await checkJiraStatus();
      setJiraConnected(status.is_connected);
      return status.is_connected;
    } catch (err) {
      setJiraConnected(false);
      return false;
    }
  };

  const fetchJiraData = async () => {
    setLoading(true);
    // Clear any existing error messages when loading begins
    setError(null);
    
    try {
      // Add a bit of a delay to reduce race conditions
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Default project and placeholder data
      let defaultProjects = [
        { id: "default", key: "DEFAULT", name: "Default Project" }
      ];
      
      // Priorities are now enabled
      let defaultPriorities = [
        { id: "1", name: "Highest" },
        { id: "2", name: "High" },
        { id: "3", name: "Medium" },
        { id: "4", name: "Low" },
        { id: "5", name: "Lowest" }
      ];
      
      // Show loading state for projects
      setProjectsLoading(true);
      
      try {
        const projectsRes = await getJiraProjects();
        console.log("Jira projects response:", projectsRes);
        
        // Handle different response formats
        let projects = Array.isArray(projectsRes) 
          ? projectsRes 
          : projectsRes.projects || [];
          
        // Ensure we have projects
        if (projects.length === 0 && typeof projectsRes === 'object') {
          // Try to extract projects if it's directly in the response
          projects = Object.values(projectsRes).filter(item => 
            item && typeof item === 'object' && item.key && item.name
          );
        }
        
        if (projects && projects.length > 0) {
          console.log("Processed projects:", projects);
          setJiraProjects(projects);
          setSelectedProject(projects[0].key || projects[0].id);
        } else {
          console.log("Using default projects");
          setJiraProjects(defaultProjects);
          setSelectedProject(defaultProjects[0].key);
        }
      } catch (err) {
        // Silently handle errors and use defaults instead of showing error messages
        console.log("Using default projects after error:", err);
        setJiraProjects(defaultProjects);
        setSelectedProject(defaultProjects[0].key);
      } finally {
        setProjectsLoading(false);
      }
      
      // Enable priorities
      setPrioritiesLoading(true);
      try {
        // Set a timeout to simulate loading for better UX
        await new Promise(resolve => setTimeout(resolve, 300));
        setPriorities(defaultPriorities);
        setCommonSettings(prev => ({
          ...prev,
          priority: defaultPriorities[0].id
        }));
      } finally {
        setPrioritiesLoading(false);
      }
      
    } catch (err) {
      // Don't show error messages for loading data, just use defaults
      console.log("Using defaults after error in fetchJiraData:", err);
    } finally {
      setLoading(false);
    }
  };

  // Check if priority is available when project changes
  useEffect(() => {
    // Only check if we have project selected
    if (selectedProject && jiraConnected) {
      const checkPriority = async () => {
        try {
          // Always set priority available to true
          setIsPriorityAvailable(true);
          setPriorityChecked(true);
        } catch (err) {
          // Default to showing priority field
          setIsPriorityAvailable(true);
        }
      };
      
      checkPriority();
    }
  }, [selectedProject]);

  // Add a new useEffect to set default priorities when they're loaded
  useEffect(() => {
    // If priorities are loaded and not empty
    if (priorities.length > 0) {
      // Set default priority for common settings
      setCommonSettings(prev => ({
        ...prev,
        priority: prev.priority || priorities[0].id
      }));
      
      // Set default priority for individual settings
      if (!isSingleMode) {
        const updatedSettings = {...individualSettings};
        selectedTestCases.forEach(tc => {
          if (updatedSettings[tc.id]) {
            updatedSettings[tc.id] = {
              ...updatedSettings[tc.id],
              priority: updatedSettings[tc.id].priority || priorities[0].id
            };
          }
        });
        setIndividualSettings(updatedSettings);
      }
    }
  }, [priorities, isSingleMode]);

  // Add useEffect to copy common settings to individual settings when user toggles
  useEffect(() => {
    if (!useSameSettings && !isSingleMode) {
      // When user toggles to individual settings mode, 
      // copy the common settings to all individual settings as a starting point
      const updatedSettings = {...individualSettings};
      
      selectedTestCases.forEach(tc => {
        // Only update if there are no existing values or they're empty
        const current = updatedSettings[tc.id] || {};
        updatedSettings[tc.id] = {
          title: current.title || commonSettings.title,
          priority: current.priority || commonSettings.priority,
          additionalDetails: current.additionalDetails || commonSettings.additionalDetails
        };
      });
      
      setIndividualSettings(updatedSettings);
    }
  }, [useSameSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!selectedProject) {
        throw new Error("Please select a Jira project");
      }

      // Validate required fields in common settings
      if (useSameSettings && !commonSettings.title.trim()) {
        throw new Error("Please enter a title for the Jira issue");
      }

      // Validate required fields in individual settings if not using same settings
      if (!useSameSettings && !isSingleMode) {
        const missingFields = [];
        selectedTestCases.forEach(tc => {
          const settings = individualSettings[tc.id] || {};
          if (!settings.title?.trim()) {
            missingFields.push(`Title for test case ${tc.id}`);
          }
          if (!settings.priority) {
            missingFields.push(`Priority for test case ${tc.id}`);
          }
        });
        
        if (missingFields.length > 0) {
          throw new Error(`Please fill in all required fields: ${missingFields.join(', ')}`);
        }
      }

      // Show a user-friendly loading message in the UI
      const loadingElement = document.getElementById('submit-loading-message');
      if (loadingElement) {
        loadingElement.textContent = "Connecting to Jira...";
      }

      // Find the selected project to get both key and id
      const selectedProjectObj = jiraProjects.find(p => 
        p.key === selectedProject || p.id === selectedProject
      );
      
      // Find the selected priority
      const selectedPriority = priorities.find(p => 
        p.id === commonSettings.priority || p.name === commonSettings.priority
      );

      // Update loading message
      if (loadingElement) {
        loadingElement.textContent = "Preparing ticket data...";
      }

      // Prepare a payload that matches the format shown in your Swagger success
      const payload = {
        project_key: selectedProjectObj?.key || selectedProject,
        // Task is hardcoded in the API now
        priority: selectedPriority?.name,
        summary: commonSettings.title.trim(),
        description: commonSettings.additionalDetails || "No details provided.",
        additional_details: commonSettings.additionalDetails || "No details provided."
      };

      // Update loading message
      if (loadingElement) {
        loadingElement.textContent = "Creating Jira ticket(s)...";
      }

      // For better UX, show estimated time for bulk operations
      const estimatedTimePerTicket = 5; // seconds
      const totalEstimatedTime = !isSingleMode && !useSameSettings ? 
        selectedTestCases.length * estimatedTimePerTicket : 
        estimatedTimePerTicket;

      // Initialize progress tracking
      const totalTickets = isSingleMode ? 1 : selectedTestCases.length;
      setProgress({ current: 0, total: totalTickets, inProgress: true });

      // Create a placeholder ticket response for immediate display
      const createPlaceholderTicket = (summary, index = 0) => ({
        key: `CREATING-${index + 1}`,
        url: '#',
        summary: summary,
        issue_type: "Task",
        priority: "Processing...",
        project_name: selectedProjectObj?.name || "Processing...",
        status: "Creating...",
        processing: true,
        progress: 0  // 0-100 percentage
      });

      // Show immediate success and process tickets in background
      if (isSingleMode) {
        // Single test case mode - show placeholder first
        const placeholderTicket = createPlaceholderTicket(commonSettings.title.trim());
        setCreatedTickets([placeholderTicket]);
        
        // Set success state to allow user to dismiss the modal
        setSuccess(true);
        
        // Call onSuccess immediately to update UI
        if (onSuccess) {
          onSuccess();
        }
        
        // Then process actual API call in background
        setTimeout(async () => {
          try {
            setProgress({ current: 0, total: 1, inProgress: true });
            
            // Update placeholder to show 50% progress (API call started)
            const updatedPlaceholder = { ...placeholderTicket, progress: 50 };
            setCreatedTickets([updatedPlaceholder]);
            
            const response = await createJiraTicket(singleTestCaseId, payload);
            
            // Update the placeholder with actual data when it's ready
            const realTicket = {
              key: response.issue_key || response.key,
              url: response.issue_url || response.url,
              summary: commonSettings.title.trim(),
              issue_type: "Task",
              priority: response.priority || "Default priority",
              project_name: response.project_name || response.project_key || "Default project",
              processing: false,
              progress: 100
            };
            
            setCreatedTickets([realTicket]);
            setProgress({ current: 1, total: 1, inProgress: false });
            
            // Force UI refresh 
            if (onSuccess) {
              onSuccess();
            }
          } catch (err) {
            console.error("Background Jira ticket creation failed:", err);
            
            // Update UI to show failure
            const failedTicket = {
              ...placeholderTicket,
              key: "FAILED-1",
              summary: `${commonSettings.title.trim()} (Failed to create)`,
              priority: "Error",
              status: "Failed",
              processing: false,
              progress: 100
            };
            
            setCreatedTickets([failedTicket]);
            setProgress({ current: 1, total: 1, inProgress: false });
            
            // Don't show error to user since we already showed success
          }
        }, 100);
      } else {
        // Bulk mode
        if (useSameSettings) {
          // Same settings for all test cases
          // Show placeholder tickets immediately
          const placeholderTickets = selectedTestCases.map((_, index) => 
            createPlaceholderTicket(commonSettings.title.trim(), index)
          );
          setCreatedTickets(placeholderTickets);
          
          // Set success state to allow user to dismiss the modal
          setSuccess(true);
          
          // Call onSuccess immediately to update UI
          if (onSuccess) {
            onSuccess();
          }
          
          // Actual API call in background
          setTimeout(async () => {
            try {
              setProgress({ current: 0, total: placeholderTickets.length, inProgress: true });
              
              // Update placeholders to show they're being processed
              const updatedPlaceholders = placeholderTickets.map(ticket => ({
                ...ticket,
                progress: 30
              }));
              setCreatedTickets(updatedPlaceholders);
              
              const bulkPayload = {
                ...payload,
                test_ids: selectedTestCases.map(tc => tc.test_case_id || tc.id)
              };
              
              const response = await createBulkJiraTickets(bulkPayload);
              
              // Handle different response formats
              let tickets = [];
              
              if (Array.isArray(response)) {
                tickets = response;
              } else if (response.tickets && Array.isArray(response.tickets)) {
                tickets = response.tickets;
              } else if (typeof response === 'object') {
                // Single ticket object
                tickets = [response];
              }
              
              // Map actual tickets to placeholder positions
              let realTickets = [];
              
              if (tickets.length === placeholderTickets.length) {
                // Perfect 1:1 mapping
                realTickets = tickets.map((ticket, idx) => ({
                  key: ticket.issue_key || ticket.key || 'UNKNOWN',
                  url: ticket.issue_url || ticket.url || '#',
                  summary: commonSettings.title.trim(),
                  issue_type: "Task",
                  priority: ticket.priority || "Default priority",
                  project_name: ticket.project_name || ticket.project_key || "Default project",
                  processing: false,
                  progress: 100
                }));
              } else {
                // Fallback if number of tickets doesn't match
                realTickets = placeholderTickets.map((placeholder, idx) => {
                  if (idx < tickets.length) {
                    const ticket = tickets[idx];
                    return {
                      key: ticket.issue_key || ticket.key || 'UNKNOWN',
                      url: ticket.issue_url || ticket.url || '#',
                      summary: commonSettings.title.trim(),
                      issue_type: "Task",
                      priority: ticket.priority || "Default priority",
                      project_name: ticket.project_name || ticket.project_key || "Default project",
                      processing: false,
                      progress: 100
                    };
                  } else {
                    // If we don't have a ticket for this placeholder, mark as failed
                    return {
                      ...placeholder,
                      key: `FAILED-${idx+1}`,
                      summary: `${placeholder.summary} (Failed to create)`,
                      priority: "Error",
                      status: "Failed",
                      processing: false,
                      progress: 100
                    };
                  }
                });
              }
              
              setCreatedTickets(realTickets);
              setProgress({ current: placeholderTickets.length, total: placeholderTickets.length, inProgress: false });
              
              // Force UI refresh
              if (onSuccess) {
                onSuccess();
              }
            } catch (err) {
              console.error("Background Jira bulk ticket creation failed:", err);
              
              // Mark all placeholders as failed
              const failedTickets = placeholderTickets.map((placeholder, idx) => ({
                ...placeholder,
                key: `FAILED-${idx+1}`,
                summary: `${placeholder.summary} (Failed to create)`,
                priority: "Error",
                status: "Failed",
                processing: false,
                progress: 100
              }));
              
              setCreatedTickets(failedTickets);
              setProgress({ current: placeholderTickets.length, total: placeholderTickets.length, inProgress: false });
              
              // Don't show error to user since we already showed success
            }
          }, 100);
        } else {
          // Individual settings for each test case
          // Show placeholder tickets immediately
          const placeholderTickets = selectedTestCases.map((testCase, index) => {
            const settings = individualSettings[testCase.id] || {};
            return createPlaceholderTicket(settings.title.trim(), index);
          });
          
          setCreatedTickets(placeholderTickets);
          
          // Set success state to allow user to dismiss the modal
          setSuccess(true);
          
          // Call onSuccess immediately to update UI
          if (onSuccess) {
            onSuccess();
          }
          
          // Process individual tickets in background 
          setTimeout(async () => {
            const createdTicketsArray = [...placeholderTickets]; // Start with placeholders
            setProgress({ current: 0, total: placeholderTickets.length, inProgress: true });
            
            try {
              // Process tickets sequentially but in background
              for (let i = 0; i < selectedTestCases.length; i++) {
                const testCase = selectedTestCases[i];
                const testId = testCase.test_case_id || testCase.id;
                const settings = individualSettings[testCase.id] || {};
                
                // Update progress for this ticket (starting)
                createdTicketsArray[i] = {
                  ...createdTicketsArray[i],
                  progress: 30,
                  status: "Processing..."
                };
                setCreatedTickets([...createdTicketsArray]);
                setProgress(prev => ({ ...prev, current: i }));
                
                // Find the selected priority for this test case
                const testCasePriority = priorities.find(p => 
                  p.id === settings.priority || p.name === settings.priority
                );
                
                const individualPayload = {
                  project_key: selectedProjectObj?.key || selectedProject,
                  priority: testCasePriority?.name,
                  summary: settings.title.trim(),
                  description: settings.additionalDetails || "No details provided.",
                  additional_details: settings.additionalDetails || "No details provided."
                };
                
                try {
                  // Update progress (API call started)
                  createdTicketsArray[i] = {
                    ...createdTicketsArray[i],
                    progress: 50
                  };
                  setCreatedTickets([...createdTicketsArray]);
                  
                  // Create individual ticket
                  const response = await createJiraTicket(testId, individualPayload);
                  
                  // Update this placeholder with real data
                  createdTicketsArray[i] = {
                    key: response.issue_key || response.key || 'UNKNOWN',
                    url: response.issue_url || response.url || '#',
                    summary: settings.title.trim(),
                    issue_type: "Task",
                    priority: response.priority || testCasePriority?.name || "Default priority",
                    project_name: response.project_name || response.project_key || "Default project",
                    processing: false,
                    progress: 100,
                    status: "Completed"
                  };
                  
                  // Update UI after each ticket is created
                  setCreatedTickets([...createdTicketsArray]);
                  setProgress(prev => ({ ...prev, current: i + 1 }));
                  
                  // Force UI refresh after each ticket
                  if (onSuccess) {
                    onSuccess();
                  }
                } catch (ticketErr) {
                  console.error(`Error creating ticket ${i+1}:`, ticketErr);
                  // Leave placeholder for failed tickets
                  createdTicketsArray[i] = {
                    ...createdTicketsArray[i],
                    key: `FAILED-${i+1}`,
                    summary: `${settings.title.trim()} (Failed to create)`,
                    priority: "Error",
                    status: "Failed",
                    processing: false,
                    progress: 100
                  };
                  
                  setCreatedTickets([...createdTicketsArray]);
                  setProgress(prev => ({ ...prev, current: i + 1 }));
                }
              }
              
              // All done
              setProgress(prev => ({ ...prev, inProgress: false }));
            } catch (err) {
              console.error("Background processing of individual tickets failed:", err);
              setProgress(prev => ({ ...prev, inProgress: false }));
            }
          }, 100);
        }
      }

      // The success UI is already shown above before async processing
      
    } catch (err) {
      // Show error message 
      setError(err.message || "Failed to create Jira ticket");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    // Call onSuccess again to ensure tickets are refreshed when modal is closed
    if (success && onSuccess) {
      onSuccess();
    }
    
    // Don't reset success state or created tickets if they're still being processed
    if (!progress.inProgress) {
      setSuccess(false);
      setCreatedTickets([]);
    }
    
    setError(null);
    onClose();
  };

  const handleIndividualSettingChange = (testCaseId, field, value) => {
    setIndividualSettings(prev => ({
      ...prev,
      [testCaseId]: {
        ...prev[testCaseId],
        [field]: value
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl flex flex-col max-h-[90vh]">
        {/* Modal Header - Fixed */}
        <div className="border-b px-6 py-4 bg-white flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Create Jira {isSingleMode ? "Issue" : "Issues"}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {isSingleMode 
                ? "Creating a ticket for selected test case" 
                : `Creating tickets for ${testCaseCount} selected test case${testCaseCount !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button 
            onClick={handleCloseModal}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body - Scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-4" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {!jiraConnected ? (
            <div className="bg-blue-50 text-blue-700 p-4 rounded-md mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Jira connection required</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      You need to connect your Jira account before creating tickets. 
                      Please go to the <a href="/me/integrations" className="font-medium underline">Integrations page</a> to connect your Jira account.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600" id="submit-loading-message">Creating Jira tickets...</span>
            </div>
          ) : success ? (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 w-full">
                  <h3 className="text-sm font-medium text-green-800">Jira {isSingleMode ? "ticket" : "tickets"} created successfully!</h3>
                  
                  {progress.inProgress && progress.total > 1 && (
                    <div className="mt-2">
                      <div className="bg-gray-200 h-2 rounded-full overflow-hidden">
                        <div 
                          className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-in-out" 
                          style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-right">
                        Processing {progress.current} of {progress.total} tickets
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-2 text-sm text-green-700">
                    <p>
                      {createdTickets.some(ticket => ticket.processing) 
                        ? `Creating ${createdTickets.length} Jira ${createdTickets.length === 1 ? "ticket" : "tickets"} (processing in background)...` 
                        : `Created ${createdTickets.length} Jira ${createdTickets.length === 1 ? "ticket" : "tickets"}:`}
                    </p>
                    <div className="mt-3 space-y-3">
                      {createdTickets.map((ticket, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden bg-white">
                          <div className={`p-3 ${ticket.processing ? 'bg-blue-100' : 'bg-blue-50'} border-b flex justify-between items-center`}>
                            <div className="flex items-center">
                              {ticket.processing ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                              ) : (
                                <svg className="h-5 w-5 text-blue-600 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#2684FF"/>
                                  <path d="M4 7V17L12 22L20 17V7" stroke="#2684FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                              <span className="font-medium text-blue-700">{ticket.key}</span>
                            </div>
                            <div>
                              {ticket.processing ? (
                                <span className="text-sm bg-blue-200 text-blue-800 py-1 px-2 rounded-md">
                                  Processing...
                                </span>
                              ) : ticket.key.startsWith('FAILED') ? (
                                <span className="text-sm bg-red-200 text-red-800 py-1 px-2 rounded-md">
                                  Creation Failed
                                </span>
                              ) : (
                                <a 
                                  href={ticket.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center bg-white border border-blue-300 rounded-md px-2 py-1 transition-colors hover:bg-blue-100"
                                >
                                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  View in Jira
                                </a>
                              )}
                            </div>
                          </div>
                          <div className="p-3">
                            {/* Show individual progress bar for processing tickets */}
                            {ticket.processing && ticket.progress > 0 && (
                              <div className="mb-2">
                                <div className="bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                  <div 
                                    className="bg-blue-500 h-full rounded-full" 
                                    style={{ width: `${ticket.progress}%` }}
                                  ></div>
                                </div>
                              </div>
                            )}
                          
                            <h4 className="font-medium text-gray-900">{ticket.summary || "Jira ticket"}</h4>
                            <div className="mt-2 text-sm text-gray-600">
                              <div className="mt-2 text-xs text-blue-600">
                                <ul className="list-disc pl-5 space-y-1">
                                  <li>Type: Task</li>
                                  <li>Priority: {ticket.priority || "Default priority"}</li>
                                  <li>Project: {ticket.project_name || ticket.project_key || "Default project"}</li>
                                  {ticket.status && <li>Status: {ticket.status}</li>}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-xs rounded bg-blue-50 p-2 text-blue-700">
                      {createdTickets.some(ticket => ticket.processing) 
                        ? "Your tickets are being created in the background. You can safely close this dialog and continue working." 
                        : "Your tickets have been created with the selected priority. You can update any additional fields directly in Jira if needed."}
                    </p>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={handleCloseModal}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-800 p-4 rounded-md mb-6">
              <div className="flex justify-between">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 00-1.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error creating Jira ticket</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
                <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <form id="jiraTicketForm">
              {/* Common Settings */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Ticket Settings
                </h3>
                
                {/* Add toggle for bulk test mode */}
                {!isSingleMode && (
                  <div className="bg-blue-50 p-4 rounded-md mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="useSameSettings"
                            type="checkbox"
                            checked={useSameSettings}
                            onChange={() => setUseSameSettings(!useSameSettings)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="useSameSettings" className="font-medium text-gray-700">
                            Use same settings for all {testCaseCount} test cases
                          </label>
                          <p className="text-gray-500">
                            Toggle off to configure each test case individually
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className={`bg-gray-50 p-4 rounded-md mb-4 ${!useSameSettings ? "opacity-50" : ""}`}>
                  <div className="flex items-center text-sm text-gray-700 mb-3">
                    <svg className="h-5 w-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>
                      All tickets will be created with type: <strong>Task</strong>
                    </span>
                  </div>
                
                  {/* Title field */}
                  <div className="mb-4">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                      Title/Summary *
                    </label>
                    <input
                      id="title"
                      type="text"
                      value={commonSettings.title}
                      onChange={(e) => setCommonSettings({...commonSettings, title: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Enter a title for the Jira ticket"
                      required
                      disabled={!useSameSettings && !isSingleMode}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="project" className="block text-sm font-medium text-gray-700 mb-1">
                        Project *
                      </label>
                      {projectsLoading ? (
                        <div className="flex items-center space-x-2 text-gray-500 text-sm py-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                          <span>Loading projects...</span>
                        </div>
                      ) : (
                        <select
                          id="project"
                          value={selectedProject}
                          onChange={(e) => setSelectedProject(e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          required
                        >
                          <option value="">Select Project</option>
                          {jiraProjects.map((project) => (
                            <option key={project.id || project.key} value={project.key || project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                        Priority *
                      </label>
                      {prioritiesLoading ? (
                        <div className="flex items-center space-x-2 text-gray-500 text-sm py-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                          <span>Loading priorities...</span>
                        </div>
                      ) : (
                        <select
                          id="priority"
                          value={commonSettings.priority}
                          onChange={(e) => setCommonSettings({...commonSettings, priority: e.target.value})}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          required
                          disabled={!useSameSettings && !isSingleMode}
                        >
                          <option value="">Select Priority</option>
                          {priorities.map((priority) => (
                            <option key={priority.id} value={priority.id}>
                              {priority.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="additionalDetails" className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Details
                    </label>
                    <textarea
                      id="additionalDetails"
                      value={commonSettings.additionalDetails}
                      onChange={(e) => setCommonSettings({...commonSettings, additionalDetails: e.target.value})}
                      rows={4}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Add any additional information about this issue..."
                      disabled={!useSameSettings && !isSingleMode}
                    />
                  </div>
                </div>
              </div>

              {/* Add Individual Test Settings if toggled */}
              {!isSingleMode && !useSameSettings && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Individual Test Case Settings
                  </h3>
                  
                  <div className="space-y-6">
                    {selectedTestCases.map((testCase, index) => (
                      <div key={testCase.id} className="bg-gray-50 p-4 rounded-md border border-gray-200">
                        <div className="flex items-center mb-3">
                          <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded mr-2">
                            Test #{index + 1}
                          </span>
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {testCase.testname || `Test case ${testCase.id}`}
                          </h4>
                        </div>
                        
                        {/* Individual title field */}
                        <div className="mb-4">
                          <label 
                            htmlFor={`title-${testCase.id}`} 
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Title/Summary *
                          </label>
                          <input
                            id={`title-${testCase.id}`}
                            type="text"
                            value={individualSettings[testCase.id]?.title || ""}
                            onChange={(e) => handleIndividualSettingChange(testCase.id, 'title', e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Enter a title for this test case"
                            required
                          />
                        </div>
                        
                        {/* Individual priority field */}
                        <div className="mb-4">
                          <label 
                            htmlFor={`priority-${testCase.id}`} 
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Priority *
                          </label>
                          <select
                            id={`priority-${testCase.id}`}
                            value={individualSettings[testCase.id]?.priority || ""}
                            onChange={(e) => handleIndividualSettingChange(testCase.id, 'priority', e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            required
                          >
                            <option value="">Select Priority</option>
                            {priorities.map((priority) => (
                              <option key={priority.id} value={priority.id}>
                                {priority.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Individual additional details field */}
                        <div>
                          <label 
                            htmlFor={`additionalDetails-${testCase.id}`} 
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Additional Details
                          </label>
                          <textarea
                            id={`additionalDetails-${testCase.id}`}
                            value={individualSettings[testCase.id]?.additionalDetails || ""}
                            onChange={(e) => handleIndividualSettingChange(testCase.id, 'additionalDetails', e.target.value)}
                            rows={3}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Add any additional information about this test case..."
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer Buttons - Fixed at bottom */}
        {!loading && !success && !error && jiraConnected && (
          <div className="px-6 py-3 border-t bg-white flex-shrink-0">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={(e) => handleSubmit(e)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Jira Ticket'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JiraTicketModal; 